import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import i18n, { changeLanguage } from '../i18n';
import logoDark from '@assets/blanco.png';
import logoLight from '@assets/negro.png';
import { IconMoon, IconSun, FlagEN, FlagMX } from '../components/Icons';
import { useState } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';

export default function LoginPage() {
  const { signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  
  // Lógica corregida: logo blanco para alto contraste oscuro, logo negro para alto contraste claro
  const isDarkTheme = theme === 'high-contrast-dark';
  const logo = isDarkTheme ? logoDark : logoLight;
  
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleLogin = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
      navigate('/', { replace: true });
    } finally {
      setIsSigningIn(false);
    }
  };

  const toggleLang = () => changeLanguage(lang === 'es' ? 'en' : 'es');

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      {isSigningIn && <LoadingOverlay message={t('loading')} />}
      <div
        className="card"
        style={{ width: '100%', maxWidth: 440, padding: 24 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={logo}
              alt="Logo"
              style={{ width: 44, height: 44, borderRadius: 8 }}
            />
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: 'var(--text-default)',
                  fontWeight: 700,
                }}
              >
                {t('login.companyName')}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                }}
              >
                {t('login.subtitle')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-icon"
              aria-label={
                theme === 'high-contrast-light' ? t('accessibility.toggleDarkTheme') : t('accessibility.toggleLightTheme')
              }
              onClick={toggleTheme}
            >
              {theme === 'high-contrast-light' ? <IconMoon /> : <IconSun />}
            </button>
            <button
              className="btn btn-icon"
              aria-label={lang === 'es' ? t('accessibility.switchToEnglish') : t('accessibility.switchToSpanish')}
              onClick={toggleLang}
            >
              {lang === 'es' ? <FlagEN /> : <FlagMX />}
            </button>
          </div>
        </div>

        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          {t('login.prompt')}
        </p>

        <button
          onClick={handleLogin}
          className="btn btn-google"
          style={{ borderRadius: 12 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 48 48"
          >
            <path
              fill="currentColor"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.841 1.156 7.961 3.039l5.657-5.657C33.045 6.053 28.728 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="currentColor"
              d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.841 1.156 7.961 3.039l5.657-5.657C33.045 6.053 28.728 4 24 4C16.318 4 9.656 8.636 6.306 14.691z"
            />
            <path
              fill="currentColor"
              d="M24 44c4.643 0 8.85-1.782 12.047-4.81l-5.563-4.668C28.42 36.521 26.309 37 24 37c-5.202 0-9.61-3.334-11.234-7.976l-6.532 5.025C9.517 40.632 16.227 44 24 44z"
            />
            <path
              fill="currentColor"
              d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.131 5.571l..003-.002l6.734 5.188C35.465 40.416 40 34.667 40 27.5c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
          {t('login.google')}
        </button>
      </div>
    </div>
  );
}
